import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { getExandSideInputStatus, handleExpandSideInput } from 'redux/BuilderModeSlice';
import {
  FetchBlockItemDimensions,
  fetchBlock,
  getIndicatorTableData,
  getShowNonEditableColumns,
  toggleShowNonEditableColumns,
} from 'redux/PlanPageSlice';
import {
  getBaseScenario as getBaseScenarioFromPlannerModeSlice,
  getSelectedIndicators,
} from 'redux/PlannerModeSlice';
import { fetchAllScenarios } from 'redux/ScenarioSlice';
import { accent, neutral, text as textToken } from 'styles/revampTokens';
import MultiDimensionTableOpzV2 from 'pages/PlanPageDetailsOPZ/Inputs/IndicatorPageMultiDim/MultiDimensionTableOpzV2';
import { monthNames } from 'pages/PlanPageDetailsOPZ/Inputs/IndicatorPageMultiDim/MultiDimensionTableOpzV2.utils';
import MultiDimensionTableOpz from 'pages/PlanPageDetailsOPZ/Inputs/IndicatorPageMultiDim/MultiDimensionTableOpz';

import FigmaExpandViewIcon from '../icons/FigmaExpandViewIcon';

import 'pages/PlanRevamp/Inputs/IndicatorPageMultiDim/Table.scss';

import MultiDimSetting from './MultiDimSetting';

interface Properties {
  refetchRequest: () => void;
  isPercentage: any;
  flags?: any;
}

const isHeaderNonEditable = (header: any, date?: string): boolean => {
  if (header.is_dimension) return false;
  if (!date) return false;

  if (header.name.startsWith('FY')) {
    const scenarioYear = Number(date);
    if (!Number.isFinite(scenarioYear)) return false;
    return Number(header.name.slice(2, 4)) + 2000 < scenarioYear;
  }

  const headingParts = String(header.name).split('-');
  const scenarioParts = date.split('-');
  if (headingParts.length < 2 || scenarioParts.length < 2) return false;

  const headingYear = Number(headingParts[1]);
  const scenarioYear = Number(scenarioParts[1]);
  if (!Number.isFinite(headingYear) || !Number.isFinite(scenarioYear)) return false;

  if (headingYear < scenarioYear) return true;
  return (
    headingYear === scenarioYear &&
    monthNames.indexOf(headingParts[0]) <= monthNames.indexOf(scenarioParts[0])
  );
};

const IndicatorTable = ({ refetchRequest, isPercentage, flags }: Properties) => {
  const dispatch: any = useDispatch();
  const { modelId, blockId } = useParams();
  // const indicatorTableData = useSelector(getIndica)
  const currentDriver = useSelector(getSelectedIndicators);

  // const dimensionItems = useSelector(getDimensionItems);
  // const plannerModel = useSelector(getPlannerModel);
  // const plannerBlock = useSelector(getPlannerBlock);
  // const dataInputsValue: any = useSelector(getDataInput);
  // const isExpand = useSelector(getExpandValue);
  // const currentDimensionIndex = useSelector(getCurrentDimensionIndex);
  const { pathname } = useLocation();
  const isExpand = useSelector(getExandSideInputStatus);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const showNonEditableColumns = useSelector(getShowNonEditableColumns);
  const baseScenario = useSelector(getBaseScenarioFromPlannerModeSlice);

  const hasAnyNonEditableValueColumns = useMemo(() => {
    if (!indicatorTableData?.column_headers?.length) return false;

    const startDate = new Date(baseScenario?.start_date);
    startDate.setMonth(startDate.getMonth() - 1);
    const yearFormat = startDate.getFullYear();
    const monthFormat = `${monthNames[startDate.getMonth()]}-${String(yearFormat).slice(-2)}`;
    const targetDate = indicatorTableData?.type === 'raw' ? monthFormat : String(yearFormat);

    return indicatorTableData.column_headers.some((header: any) =>
      isHeaderNonEditable(header, targetDate),
    );
  }, [indicatorTableData?.column_headers, indicatorTableData?.type, baseScenario?.start_date]);

  useEffect(() => {
    if (modelId && blockId) {
      dispatch(fetchBlock(Number(blockId)));
      dispatch(
        FetchBlockItemDimensions({
          blockId,
        }),
      );
      dispatch(fetchAllScenarios(Number(modelId)));
    }
  }, [pathname]);

  // useEffect(() => {
  //   if (currentDriver && baseScenario) {
  //     dispatch(
  //       FetchPlangPageIndicatorTableData({
  //         indicator_id: currentDriver?.id,
  //         parameters: {
  //           scenario_id: baseScenario?.id,
  //           format_excel_data: true,
  //         },
  //       }),
  //     );
  //   }
  // }, [currentDriver, baseScenario?.id, isExpand]);

  // useEffect(() => {
  //   if (isExpand) refetchRequest();
  // }, [isExpand]);

  // const commonObjects =
  //   plannerBlock?.dimensions?.length > 1
  //     ? plannerModel?.dimensions?.filter((object1: any) =>
  //         plannerBlock?.dimensions?.some(
  //           (object2: any) => object1.id === object2.id && object1.name !== 'Time',
  //         ),
  //       )
  //     : plannerModel?.dimensions?.filter((object1: any) =>
  //         plannerBlock?.dimensions?.some((object2: any) => object1.id === object2.id),
  //       );

  // const dimensionId =
  //   currentDriver?.data_input?.dimensions[currentDimensionIndex] || commonObjects[0]?.id;
  // const fetchDimItemsData = () => {
  //   dispatch(
  //     fetchDimensionItems({
  //       dimensionId,
  //       parameters: {
  //         scenario_id: baseScenario?.id,
  //       },
  //     }),
  //   );
  // };
  // useEffect(() => {
  //   if (currentDriver && currentDriver?.data_input?.dimensions?.length > 0) {
  //     fetchDimItemsData();
  //   }
  // }, [pathname, currentDriver?.id, baseScenario?.id, currentDimensionIndex, dimensionId]);
  // useEffect(() => {
  //   if (currentDriver) {
  //     fetchDimItemsData();
  //   }
  // }, [pathname, dimensionId]);
  // useEffect(() => {
  //   dispatch(
  //     FetchDataInput({
  //       indicator_id: currentDriver?.id,
  //       parameters: {
  //         scenario_id: baseScenario?.id,
  //         format_excel_data: true,
  //       },
  //     }),
  //   );
  // }, [indicator?.input[0].type, plannerBlock?.dimensions?.length > 1, baseScenario]);
  // useEffect(() => {
  //   dispatch(setExpand(dataInputsValue?.dimensions?.length > 0));
  // }, [dataInputsValue?.dimensions]);

  if (!currentDriver) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 4 }}>
        {[...new Array(6)].map((_, index) => (
          <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: 8 }}>
            {[...new Array(4)].map((__, subIndex: number) => (
              <div
                key={subIndex}
                style={{
                  background: neutral.surfaceHover,
                  flex: 1,
                  height: 35,
                  marginRight: 8,
                }}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* One toolbar row, not three controls spread over two lines: the view
          toggle sits on the left, and the two things that change what the
          table shows — dimensions and actuals — group together on the right.
          "Expand view" was bare text with no affordance, so it read as a
          caption rather than a control. */}
      <div
        style={{
          display: 'flex',
          flexDirection: isExpand ? 'row-reverse' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
          marginTop: 6,
        }}
      >
        <button
          type='button'
          onClick={() => dispatch(handleExpandSideInput(!isExpand))}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 28,
            padding: '0 10px',
            border: `1px solid ${neutral.border}`,
            borderRadius: 8,
            background: neutral.surface,
            color: textToken.secondary,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <FigmaExpandViewIcon collapse={isExpand} color={textToken.secondary} />
          {isExpand ? 'Collapse view' : 'Expand view'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <MultiDimSetting indicatorId={currentDriver?.id} refetchRequest={refetchRequest} />
          {hasAnyNonEditableValueColumns && (
            <button
              type='button'
              onClick={() => dispatch(toggleShowNonEditableColumns())}
              style={{
                height: 28,
                padding: '0 10px',
                border: `1px solid ${showNonEditableColumns ? accent.border : neutral.border}`,
                borderRadius: 8,
                background: showNonEditableColumns ? accent.subtle : neutral.surface,
                color: showNonEditableColumns ? accent.hover : textToken.secondary,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {showNonEditableColumns ? 'Hide actuals' : 'Show actuals'}
            </button>
          )}
        </div>
      </div>
      {/* Opts this panel into the light table theme by name; without a
          scope class the shared grid falls back to the old purple one. */}
      <div className='builder-revamp-md-table' style={{ height: 'fit-content' }}>
        {!flags?.inputPanelOptimization ? (
          <MultiDimensionTableOpz
            refetchRequest={refetchRequest}
            isPercentage={isPercentage}
            isBuilderMode={true}
          />
        ) : (
          <MultiDimensionTableOpzV2
            refetchRequest={refetchRequest}
            isPercentage={isPercentage}
            isBuilderMode={true}
          />
        )}
      </div>
    </>
  );
};

export default withLDConsumer()(IndicatorTable);
